/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Your cooking groups.
 *
 * ── WHAT CHANGED ────────────────────────────────────────────────────────────
 * The old screen led with a card containing two inputs stacked together — a
 * name field and an 8-character code field — separated only by a thin rule.
 * They look identical and do opposite things: one starts a new group, the other
 * joins someone else's. That card sat above the list, so the first thing you
 * met every single visit was a form for something you do roughly twice a year,
 * and the groups you actually came to read were below it.
 *
 * Now: the groups lead. Creating and joining are one button each, they open
 * separate sheets, and the join sheet says plainly that you need a code from
 * someone already in the group — which was the missing sentence that made the
 * code field baffling.
 *
 * The empty state does the explaining instead, which is where explanation
 * belongs: it is the only moment when the user genuinely does not know what
 * this feature is.
 */

import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { CirclesApi } from "../../api/endpoints.ts";
import type { CircleSummary } from "../../api/types.ts";
import { ErrorBox, Loading, Modal, PageHeader } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";

/** Invite codes are exactly 8 characters — see generateInviteCode in circles.ts. */
const CODE_LENGTH = 8;

/** Relative time, so "which group is alive" is readable at a glance. */
function ago(iso: string | null): string {
  if (!iso) return "no posts yet";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function tone(label: string): string {
  const tones = [
    "bg-accent/15 text-accent", "bg-teal/15 text-teal", "bg-navy/15 text-navy",
    "bg-ok/15 text-ok", "bg-warn/20 text-ink-2",
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  return tones[Math.abs(hash) % tones.length]!;
}

export function CirclesListView() {
  const { t } = usePrefs();
  const toast = useToast();
  const qc = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  const circlesQuery = useQuery({
    queryKey: ["circles"],
    queryFn: () => CirclesApi.list(),
    retry: false,
  });

  const refreshCircles = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["circles"] });
  }, [qc]);

  if (circlesQuery.isLoading) return <Loading label={t("circles.loading")} />;
  if (circlesQuery.error) {
    return <ErrorBox message={(circlesQuery.error as Error).message} onRetry={refreshCircles} />;
  }

  const circles: CircleSummary[] = circlesQuery.data?.circles ?? [];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title={t("nav.circles")}
        subtitle="Share what you're cooking with other households — photos, how it went, and recipes worth passing on."
        actions={
          <>
            <button className="btn-ghost" onClick={() => setJoining(true)}>
              Join with a code
            </button>
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" className="h-4 w-4" />
              New group
            </button>
          </>
        }
      />

      {circles.length === 0 ? (
        // The one place a full explanation belongs — it is the only moment the
        // user genuinely doesn't know what this is.
        <div className="card p-8 text-center">
          <span aria-hidden className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-accent/10 text-accent">
            <Icon name="chat" className="h-6 w-6" />
          </span>
          <p className="font-medium text-ink">No cooking groups yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-ink-3">
            A cooking group is a private space shared with other households. Post a photo
            of what you made, say how it went, and share recipes worth passing on. Only
            members can see it, and posts are deleted automatically after a while.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" className="h-4 w-4" />
              Start a group
            </button>
            <button className="btn-ghost" onClick={() => setJoining(true)}>
              I have a code
            </button>
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {circles.map((c) => (
            <li key={c.id}>
              <Link
                to={`/circles/${c.id}`}
                className="card flex items-center gap-3 p-4 transition hover:border-accent"
              >
                <span aria-hidden className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl text-base font-semibold ${
                  tone(c.name)}`}>
                  {c.name.trim().charAt(0).toUpperCase() || "?"}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-ink">{c.name}</span>
                    {c.isMuted && (
                      <Icon name="alert" className="h-3.5 w-3.5 shrink-0 text-ink-3"
                            label="Muted" />
                    )}
                    {c.isLocked && (
                      <Icon name="archive" className="h-3.5 w-3.5 shrink-0 text-ink-3"
                            label={t("circles.locked")} />
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-ink-3">
                    {c.messageCount === 0
                      ? "No posts yet — be the first"
                      : `${c.messageCount} ${c.messageCount === 1 ? "post" : "posts"} · ${ago(c.lastActivityAt)}`}
                  </div>
                </div>

                {c.unreadCount > 0 && !c.isMuted && (
                  <span className="grid h-6 min-w-[1.5rem] shrink-0 place-items-center rounded-full bg-accent px-1.5 text-xs font-semibold text-paper">
                    {c.unreadCount > 99 ? "99+" : c.unreadCount}
                  </span>
                )}
                <Icon name="chevronRight" className="h-4 w-4 shrink-0 text-ink-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <CreateGroupModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); refreshCircles(); toast(t("circles.created"), "ok"); }}
        />
      )}
      {joining && (
        <JoinGroupModal
          onClose={() => setJoining(false)}
          onJoined={() => { setJoining(false); refreshCircles(); toast(t("circles.joined"), "ok"); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CreateGroupModal({ onClose, onCreated }: {
  onClose: () => void; onCreated: () => void;
}) {
  const [name, setName] = useState("");
  // Retention was fixed at 30 days and invisible. It is the single most
  // surprising property of the feature — posts disappear — so it is stated
  // here, at the moment the group is made, rather than discovered later.
  const [retentionDays, setRetentionDays] = useState(30);

  const create = useMutation({
    mutationFn: () => CirclesApi.create({ name: name.trim(), retentionDays }),
    onSuccess: onCreated,
  });

  return (
    <Modal title="Start a cooking group" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }}
      >
        <label className="block">
          <span className="text-sm font-medium">What's it called?</span>
          <input
            className="input mt-1"
            placeholder="Sunday Cooks, The Family, Book Club Dinners…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoFocus
          />
        </label>

        <fieldset>
          <legend className="text-sm font-medium">How long should posts stay?</legend>
          <p className="mb-2 text-xs text-ink-3">
            Posts and photos are deleted automatically after this. It keeps a cooking
            group a conversation rather than an archive.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[7, 30, 90].map((d) => (
              <button key={d} type="button" className="chip-toggle"
                      aria-pressed={retentionDays === d}
                      onClick={() => setRetentionDays(d)}>
                {d} days
              </button>
            ))}
          </div>
        </fieldset>

        {create.error && <ErrorBox message={(create.error as Error).message} />}

        <div className="flex gap-2">
          <button type="button" className="btn-ghost flex-1" onClick={onClose}>Cancel</button>
          <button className="btn-primary flex-1" disabled={!name.trim() || create.isPending}>
            {create.isPending ? "Creating…" : "Create group"}
          </button>
        </div>

        <p className="text-center text-xs text-ink-3">
          You'll get an invite code to send to the other households.
        </p>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function JoinGroupModal({ onClose, onJoined }: {
  onClose: () => void; onJoined: () => void;
}) {
  const [code, setCode] = useState("");

  const redeem = useMutation({
    mutationFn: () => CirclesApi.redeemInvite(code.trim()),
    onSuccess: onJoined,
  });

  return (
    <Modal title="Join a cooking group" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim().length === CODE_LENGTH) redeem.mutate();
        }}
      >
        {/* The sentence the old screen was missing. A bare 8-character field
            with a placeholder is baffling if nobody has told you the code comes
            from another person. */}
        <p className="text-sm text-ink-2">
          Someone already in the group can create an invite code for you — ask them
          for it, then type it here.
        </p>

        <label className="block">
          <span className="text-sm font-medium">Invite code</span>
          <input
            className="input mt-1 text-center font-mono text-lg tracking-[0.3em]"
            placeholder="ABCD1234"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
            maxLength={CODE_LENGTH}
            autoFocus
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
          />
          <span className="mt-1 block text-xs text-ink-3">
            {code.length}/{CODE_LENGTH} characters · codes expire after 7 days
          </span>
        </label>

        {redeem.error && <ErrorBox message={(redeem.error as Error).message} />}

        <div className="flex gap-2">
          <button type="button" className="btn-ghost flex-1" onClick={onClose}>Cancel</button>
          <button className="btn-primary flex-1"
                  disabled={code.length !== CODE_LENGTH || redeem.isPending}>
            {redeem.isPending ? "Joining…" : "Join"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
